import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import type { FactoryImage } from '@indutrust/factory-model';
import { EnvironmentService } from '@indutrust/shared-env';
import { BaseApiClient } from '@indutrust/shared-model';
import type { Observable } from 'rxjs';

/**
 * API client for the factory-scoped image sub-resource at
 * `/factories/{factoryId}/images`.
 *
 * Call the inherited CRUD methods with
 * `urlSegments: { factoryId: <id> }` to bind the URL to a specific factory.
 *
 * For binary uploads use {@link FactoryImageApiClient.upload | upload}
 * — the inherited `add` posts JSON, which the backend won't accept for
 * the actual file payload.
 *
 * Gallery display order is persisted as part of the main factory PATCH
 * (field `gallery_image_ids`) rather than via a dedicated reorder endpoint.
 */
@Service()
export class FactoryImageApiClient extends BaseApiClient<FactoryImage> {
  protected envService = inject(EnvironmentService);
  protected http = inject(HttpClient);

  protected template: Partial<FactoryImage> = {};

  /**
   * Uploads a binary image for a factory using `multipart/form-data`.
   *
   * @example
   * client.upload(42, file).subscribe();
   */
  upload(factoryId: number, file: File): Observable<FactoryImage> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<FactoryImage>(
      `${this.envService.getApiUrl()}/factories/${factoryId}/images`,
      form
    );
  }

  get url(): string {
    return `${this.envService.getApiUrl()}/factories/{factoryId}/images`;
  }
}
